/**
 * Annonces · un message sur les téléphones des accrédités (2026-10-22).
 *
 * Une page, un geste : écrire (français ET anglais, chaque téléphone reçoit la
 * langue de son application), choisir à qui, VÉRIFIER combien de téléphones le
 * recevront, puis envoyer. L'envoi n'est possible qu'après la vérification, et
 * toute modification du texte ou des destinataires l'annule : on envoie
 * exactement ce qu'on a vérifié.
 *
 * Le back-office ne parle pas aux téléphones : il remet l'annonce au serveur de
 * l'application, qui la pousse par Apple (Android viendra avec Firebase).
 * Réservé aux administrateurs : un message parti ne se rattrape pas.
 */

import { useMemo, useState } from 'react';
import { api, ApiError } from '../lib/api';
import type { AnnouncementDraft, AnnouncementItem, AnnouncementRecipients, AnnouncementsPage } from '../lib/api';
import { useApi } from '../lib/useApi';
import { toast } from '../components/Toast';
import { formatDateTime, formatInt } from '../lib/format';
import { lineIdentity } from '../lib/lines';
import { Async, Badge, Banner, Card } from '../components/ui';
import { READ_ONLY_NOTICE, useCan } from '../lib/session';

const CLUSTERS: { key: 'dakar' | 'diamniadio' | 'saly'; label: string }[] = [
  { key: 'dakar', label: 'Dakar' },
  { key: 'diamniadio', label: 'Diamniadio' },
  { key: 'saly', label: 'Saly' },
];

function errorText(cause: unknown): string {
  if (cause instanceof ApiError) return cause.problem.detail ?? cause.userMessage;
  return String(cause);
}

function phones(n: number): string {
  return `${formatInt(n)} téléphone${n > 1 ? 's' : ''}`;
}

export function AnnouncementsPage(): React.ReactElement {
  const [nonce, setNonce] = useState(0);
  const state = useApi((s) => api.announcements(s), `announcements|${nonce}`);
  const canSend = useCan('announcements:send');

  return (
    <div className="page">
      <Async state={state}>
        {(data) => (
          <>
            {!data.configured ? (
              <Banner tone="warn" title="L'envoi n'est pas encore branché">
                Le tableau de bord ne connaît pas encore l'adresse du serveur de l'application ou sa clé d'envoi des annonces. Vous pouvez
                préparer un message, mais rien ne partira tant que ces deux réglages ne sont pas posés.
              </Banner>
            ) : null}
            {canSend ? (
              <ComposeCard data={data} onSent={() => setNonce((n) => n + 1)} />
            ) : (
              <Card title="Nouvelle annonce">
                <p className="muted">{READ_ONLY_NOTICE} L'envoi d'une annonce est réservé aux administrateurs.</p>
              </Card>
            )}
            <HistoryCard items={data.items} lines={data.lines} />
          </>
        )}
      </Async>
    </div>
  );
}

// ─── Rédaction ───────────────────────────────────────────────────────────────

function ComposeCard(props: { data: AnnouncementsPage; onSent: () => void }): React.ReactElement {
  const { data, onSent } = props;
  const { limits, lines } = data;
  const [mode, setMode] = useState<'all' | 'lines'>('all');
  const [picked, setPicked] = useState<string[]>([]);
  const [fr, setFr] = useState({ title: '', body: '' });
  const [en, setEn] = useState({ title: '', body: '' });
  /** Résultat de la vérification, attaché au brouillon EXACT qui a été vérifié. */
  const [checked, setChecked] = useState<{ key: string; recipients: AnnouncementRecipients; apnsConfigured: boolean } | null>(null);
  const [busy, setBusy] = useState<'check' | 'send' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const draft: AnnouncementDraft = useMemo(
    () => ({
      target: mode === 'all' ? { kind: 'all' } : { kind: 'lines', lineNos: [...picked].sort() },
      fr: { title: fr.title.trim(), body: fr.body.trim() },
      en: { title: en.title.trim(), body: en.body.trim() },
    }),
    [mode, picked, fr, en],
  );
  const draftKey = JSON.stringify(draft);
  const verified = checked !== null && checked.key === draftKey ? checked : null;

  const complete =
    draft.fr.title !== '' && draft.fr.body !== '' && draft.en.title !== '' && draft.en.body !== '' && (mode === 'all' || picked.length > 0);

  const toggle = (lineNo: string) => setPicked((prev) => (prev.includes(lineNo) ? prev.filter((l) => l !== lineNo) : [...prev, lineNo]));
  const toggleCluster = (cluster: string) => {
    const inCluster = lines.filter((l) => l.cluster === cluster).map((l) => l.lineNo);
    const allIn = inCluster.every((l) => picked.includes(l));
    setPicked((prev) => (allIn ? prev.filter((l) => !inCluster.includes(l)) : [...new Set([...prev, ...inCluster])]));
  };

  const check = async () => {
    setBusy('check');
    setError(null);
    try {
      const result = await api.previewAnnouncement(draft);
      setChecked({ key: draftKey, recipients: result.recipients, apnsConfigured: result.apnsConfigured });
    } catch (cause) {
      setError(errorText(cause));
    } finally {
      setBusy(null);
    }
  };

  const send = async () => {
    setBusy('send');
    setError(null);
    try {
      const result = await api.sendAnnouncement(draft);
      toast(`Annonce envoyée à ${phones(result.sent)}`);
      setFr({ title: '', body: '' });
      setEn({ title: '', body: '' });
      setPicked([]);
      setMode('all');
      setChecked(null);
      onSent();
    } catch (cause) {
      setError(errorText(cause));
      // Revérifier avant de renvoyer : après un délai dépassé, l'annonce est
      // peut-être partie, et un second clic en enverrait une seconde.
      setChecked(null);
      onSent();
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card
      title="Nouvelle annonce"
      hint="Chaque téléphone la reçoit dans la langue de son application. Une annonce envoyée ne peut pas être retirée."
    >
      <div className="ann-step">
        <h3>À qui ?</h3>
        <div className="seg" role="group" aria-label="Destinataires">
          <button type="button" aria-pressed={mode === 'all'} onClick={() => setMode('all')}>
            Tous les téléphones
          </button>
          <button type="button" aria-pressed={mode === 'lines'} onClick={() => setMode('lines')}>
            Certaines lignes
          </button>
        </div>
        {mode === 'lines' ? (
          <div className="ann-lines">
            {CLUSTERS.map((cluster) => {
              const inCluster = lines.filter((l) => l.cluster === cluster.key);
              if (inCluster.length === 0) return null;
              const allIn = inCluster.every((l) => picked.includes(l.lineNo));
              return (
                <div key={cluster.key} className="ann-cluster">
                  <button type="button" className="btn btn-sm" onClick={() => toggleCluster(cluster.key)}>
                    {allIn ? `Retirer ${cluster.label}` : `Tout ${cluster.label}`}
                  </button>
                  <div className="ann-chips">
                    {inCluster.map((line, index) => {
                      const identity = lineIdentity(line.lineNo, index);
                      const on = picked.includes(line.lineNo);
                      return (
                        <button
                          key={line.lineNo}
                          type="button"
                          className="ann-chip"
                          aria-pressed={on}
                          style={on ? { background: identity.color, borderColor: identity.color, color: '#fff' } : { borderColor: identity.color }}
                          onClick={() => toggle(line.lineNo)}
                        >
                          {line.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            <p className="muted small">
              Seuls les téléphones qui ont mis l'une de ces lignes en favori recevront l'annonce. Pour être sûr que tout le monde la lise,
              choisissez « Tous les téléphones ».
            </p>
          </div>
        ) : null}
      </div>

      <div className="grid grid-2 ann-texts">
        <TextBlock heading="En français" text={fr} onChange={setFr} limits={limits} placeholderTitle="Ligne YOG 1 déviée" />
        <TextBlock heading="In English" text={en} onChange={setEn} limits={limits} placeholderTitle="YOG 1 line diverted" />
      </div>

      {draft.fr.title || draft.fr.body ? <NotificationPreview title={draft.fr.title} body={draft.fr.body} /> : null}

      {error ? (
        <Banner tone="bad" title="L'annonce n'est pas partie">
          {error}
        </Banner>
      ) : null}

      {verified ? (
        <div className="ann-confirm">
          {verified.recipients.ios === 0 ? (
            <p>
              <strong>Aucun iPhone ne recevra ce message.</strong>{' '}
              {mode === 'lines' ? "Personne n'a mis ces lignes en favori, ou les téléphones ne se sont pas encore inscrits." : "Aucun téléphone n'est encore inscrit."}
            </p>
          ) : (
            <p>
              Ce message partira sur <strong>{phones(verified.recipients.ios)}</strong>.
              {verified.recipients.android > 0
                ? ` ${phones(verified.recipients.android)} Android ne le recevront pas encore.`
                : ''}
            </p>
          )}
          {!verified.apnsConfigured ? (
            <p className="muted small">Le serveur de l'application n'a pas sa clé Apple : l'envoi sera refusé.</p>
          ) : null}
          <div className="toolbar">
            <button
              type="button"
              className="btn btn-gold"
              disabled={busy !== null || verified.recipients.ios === 0 || !verified.apnsConfigured}
              aria-busy={busy === 'send'}
              onClick={() => void send()}
            >
              {busy === 'send' ? 'Envoi…' : `Envoyer maintenant à ${phones(verified.recipients.ios)}`}
            </button>
            <button type="button" className="btn" disabled={busy !== null} onClick={() => setChecked(null)}>
              Modifier
            </button>
          </div>
        </div>
      ) : (
        <div className="toolbar">
          <button
            type="button"
            className="btn btn-primary"
            disabled={!complete || busy !== null || !data.configured}
            aria-busy={busy === 'check'}
            onClick={() => void check()}
          >
            {busy === 'check' ? 'Vérification…' : 'Vérifier les destinataires'}
          </button>
          {!complete ? <span className="muted small">Titre et message dans les deux langues, puis vérifiez à qui il partira.</span> : null}
        </div>
      )}
    </Card>
  );
}

function TextBlock(props: {
  heading: string;
  text: { title: string; body: string };
  onChange: (text: { title: string; body: string }) => void;
  limits: { title: number; body: number };
  placeholderTitle: string;
}): React.ReactElement {
  const { heading, text, onChange, limits, placeholderTitle } = props;
  return (
    <div className="ann-text">
      <h3>{heading}</h3>
      <label className="field">
        <span className="ann-label">
          Titre <span className="ann-count">{text.title.length} / {limits.title}</span>
        </span>
        <input type="text" value={text.title} maxLength={limits.title} placeholder={placeholderTitle} onChange={(e) => onChange({ ...text, title: e.target.value })} />
      </label>
      <label className="field">
        <span className="ann-label">
          Message <span className="ann-count">{text.body.length} / {limits.body}</span>
        </span>
        <textarea rows={4} value={text.body} maxLength={limits.body} onChange={(e) => onChange({ ...text, body: e.target.value })} />
      </label>
    </div>
  );
}

/** La bannière telle que l'accrédité la verra sur son iPhone (texte français). */
function NotificationPreview(props: { title: string; body: string }): React.ReactElement {
  return (
    <div className="ann-preview" aria-label="Aperçu de la notification">
      <span className="muted small">Aperçu sur iPhone</span>
      <div className="ann-banner">
        <div className="ann-banner-icon">Y</div>
        <div className="ann-banner-text">
          <div className="ann-banner-top">
            <span>YOG MOVE</span>
            <span>maintenant</span>
          </div>
          <strong>{props.title || 'Titre'}</strong>
          <p>{props.body || 'Message'}</p>
        </div>
      </div>
    </div>
  );
}

// ─── Historique ──────────────────────────────────────────────────────────────

function targetLabel(item: AnnouncementItem, lines: AnnouncementsPage['lines']): string {
  if (item.target.kind === 'all') return 'Tous les téléphones';
  const names = item.target.lineNos.map((no) => lines.find((l) => l.lineNo === no)?.name ?? no);
  return names.join(', ');
}

function HistoryCard(props: { items: AnnouncementItem[]; lines: AnnouncementsPage['lines'] }): React.ReactElement {
  const { items, lines } = props;
  return (
    <Card title="Annonces envoyées" hint="La trace de ce que les accrédités ont reçu, et de qui l'a envoyé.">
      {items.length === 0 ? (
        <p className="muted">Aucune annonce envoyée pour l'instant.</p>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Envoyée</th>
                <th>Par</th>
                <th>À qui</th>
                <th>Message</th>
                <th>Résultat</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td className="nowrap">{formatDateTime(item.sentAt ?? item.createdAt)}</td>
                  <td>{item.sentBy}</td>
                  <td>{targetLabel(item, lines)}</td>
                  <td>
                    <strong>{item.fr.title}</strong>
                    <div className="muted small">{item.fr.body}</div>
                  </td>
                  <td>
                    {item.status === 'sent' ? (
                      <>
                        <Badge tone="ok">Envoyée à {phones(item.sent)}</Badge>
                        {item.error ? <div className="muted small">{item.error}</div> : null}
                      </>
                    ) : item.status === 'failed' ? (
                      <>
                        <Badge tone="bad">Non envoyée</Badge>
                        {item.error ? <div className="muted small">{item.error}</div> : null}
                      </>
                    ) : (
                      <Badge tone="warn">Envoi en cours</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
